/**
 * A caixa "Conferência — Importar Guia DAE (PDF)" de Lançamentos.
 *
 * Papel: reproduz `#modal-dae-pdf`, `abrirConferenciaDaePdf` e
 * `confirmarImportacaoDaePdf` (l. 864–893, 4467–4550): o período de apuração
 * lido, a data do lançamento no vencimento, uma linha por código da guia com o
 * valor editável, e "Confirmar e Criar Lançamentos", que cria um lançamento
 * pendente por linha marcada com valor.
 *
 * Como usar: quem monta já leu o PDF e só abre a caixa quando há linha; a caixa
 * fecha sozinha depois de gravar, chamando `onFechar`. O aviso amarelo junta as
 * duas faltas — período e vencimento —, cada uma só quando o PDF não trazia o
 * dado; sem vencimento, a data começa vazia e a confirmação a pede.
 */
import { competenciaComBarra } from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import {
  type LinhaDaConferencia,
  avisoDosCriados,
  linhasConfirmadas,
  linhasDaConferencia,
} from '../conferencia';
import { useImportarGuiaDae } from '../hooks/usar-financas-extras';
import { type GuiaDoPdf } from '../importacao/guia-dae';
import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DO_CONTROLE, Campo, LinhaDeCampos } from './kit/campo';
import { Celula } from './kit/tabela';
import { TabelaDeConferencia } from './tabela-de-conferencia';

interface ConferenciaDaGuiaProps {
  /** O que o parser leu do PDF, com ao menos uma linha. */
  guia: GuiaDoPdf;
  residenciaId: string;
  onFechar: () => void;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O aviso amarelo da guia.
 *
 * @param guia - O que o parser leu.
 * @returns As frases das faltas, separadas por espaço; vazio sem falta.
 */
function avisoDaGuia(guia: GuiaDoPdf): string {
  return [
    guia.competenciaDetectada ? null : 'Não encontramos o período de apuração no PDF.',
    guia.vencimento === null
      ? 'Não encontramos a data de vencimento — informe a data do lançamento manualmente.'
      : null,
  ]
    .filter((frase) => frase !== null)
    .join(' ');
}

/**
 * A conferência da guia DAE em PDF.
 *
 * @param guia - As linhas, o período, o vencimento e o total lidos do PDF.
 * @param residenciaId - A casa do caixa.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a
 *   gravação que deu certo.
 * @returns A caixa de 720 px.
 */
export function ConferenciaDaGuia({ guia, residenciaId, onFechar }: ConferenciaDaGuiaProps) {
  const notificar = useNotificacao();
  const importar = useImportarGuiaDae();
  const [data, setData] = useState(guia.vencimento ?? '');
  const [linhas, setLinhas] = useState<LinhaDaConferencia[]>(() =>
    linhasDaConferencia(guia.itens.map((item) => item.valor)),
  );
  const aviso = avisoDaGuia(guia);

  /** "Confirmar e Criar Lançamentos" (l. 4511–4550). */
  async function handleConfirmar() {
    if (data === '') {
      notificar('Informe a data do lançamento.', false);
      return;
    }
    const itens = linhasConfirmadas(linhas).flatMap(({ indice, valor }) => {
      const item = guia.itens[indice];
      return item === undefined
        ? []
        : [
            {
              codigo: item.codigo,
              denominacao: item.denominacao,
              subcategoria: item.subcategoria,
              valor,
            },
          ];
    });
    if (itens.length === 0) {
      notificar('Nenhum item selecionado.', false);
      return;
    }
    try {
      const { criados } = await importar.mutateAsync({
        residenciaId,
        competencia: guia.competencia,
        data,
        itens,
      });
      onFechar();
      notificar(avisoDosCriados(criados, 'da guia DAE'));
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Conferência — Importar Guia DAE (PDF)"
      largura={720}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleConfirmar()} disabled={importar.isPending}>
            Confirmar e Criar Lançamentos
          </Botao>
        </>
      }
    >
      {aviso !== '' && <AvisoDeConferencia>{aviso}</AvisoDeConferencia>}
      <LinhaDeCampos colunas={2} className="mb-1.5">
        <Campo rotulo="Período de Apuração" id="dae-pdf-competencia">
          <div id="dae-pdf-competencia" className="py-1.5 font-semibold text-foreground">
            {guia.competencia === null ? '—' : competenciaComBarra(guia.competencia)}
          </div>
        </Campo>
        <Campo rotulo="Data do lançamento (vencimento, editável)" id="dae-pdf-data">
          <input
            id="dae-pdf-data"
            type="date"
            value={data}
            onChange={(evento) => setData(evento.target.value)}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <div className="mb-2.5 text-[11px] text-texto-fraco">
        Revise cada linha da guia antes de confirmar. Desmarque quem não deve entrar. O PDF em
        si não é salvo no sistema — apenas os lançamentos abaixo, e somente ao confirmar.
      </div>
      <TabelaDeConferencia
        colunas={['Denominação', 'Subcategoria']}
        colunaDoValor="Valor (R$)"
        linhas={linhas}
        rotuloDaLinha={(indice) => guia.itens[indice]?.denominacao ?? ''}
        celulas={(indice) => (
          <>
            <Celula>
              {guia.itens[indice]?.denominacao}
              <div className="text-[11px] text-texto-fraco">
                Código {guia.itens[indice]?.codigo}
              </div>
            </Celula>
            <Celula className="text-[12px]">{guia.itens[indice]?.subcategoria}</Celula>
          </>
        )}
        totalPdf={guia.totalPdf}
        onMudar={setLinhas}
      />
    </CaixaDeDialogo>
  );
}
